import { Fragment } from "react";
import Button from "@components/Button/Button";
import ButtonFactory from "@components/Button/ButtonFactory";
import {
  COMMON_BUTTON_CSS,
  REMOVE_FOCUS_CSS,
} from "@components/Button/utils/constants";
import {
  ActionType,
  ButtonSize,
  ButtonType,
} from "@components/Button/utils/types";
import IconImg from "@components/Icon/IconImg";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { GiftAnArticleVersion } from "@features/giftAnArticle/utils/types";
import ArticleSummariseButton from "@features/toolbar/ArticleToolbar/components/ArticleSummarise";
import {
  BUTTON_CSS,
  TRACKING_OBJECT,
} from "@features/toolbar/ArticleToolbar/utils/constants";
import { ArticleAllToolPopoverProps } from "@features/toolbar/ArticleToolbar/utils/types";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import useFeedbackFormUrl from "@hooks/useFeedbackFormUrl";
import useSendDataForTracking, {
  TrackingObject,
} from "@hooks/useSendDataForTracking";
import { useWindowSize } from "@hooks/useWindowSize";
import ArticleKeywords, {
  KeywordDisplayOption,
} from "@routes/Article/components/ArticleKeywords";
import { newspostUrl } from "@routes/Article/components/ArticlePurchaseLink/constants";
import ArticleSubshare from "@routes/Article/components/ArticleSubshare/ArticleSubshare";
import { cn } from "@util/helpers";

/**
 * A React component that renders a popover containing various tools and actions
 * for interacting with an article. The component supports features such as sharing,
 * bookmarking, listening, printing, and more, depending on the provided props.
 */
export default function ArticleAllToolPopover({
  handleShareArticle,
  handleOpenModal,
  testId = "article-all-tools-popover-component",
  isPremium = false,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  alacrityRes,
  keyword,
  displayPurchase,
  cueId = "",
  className,
  isSummaryAvailable,
  isChinese = false,
}: ArticleAllToolPopoverProps): React.ReactElement {
  const { sendTrackingData } = useSendDataForTracking();
  const sourceUrl = useFeedbackFormUrl();

  const date = encodeURIComponent(displayPurchase?.publishedDate || "");
  const encodedTitle = encodeURIComponent(displayPurchase?.title || "");

  const newspostFullUrl = `${newspostUrl}?publication=BT&title=${encodedTitle}&date=${date}&utm_source=businesstimes.com.sg&utm_medium=referral&utm_campaign=reuse_this_content&utm_term=${cueId}`;

  const { isScreenLG } = useWindowSize();

  const handlePrint = () => {
    if (typeof window === "undefined") return;

    const articleDocument = window.document.querySelectorAll("article");

    articleDocument.forEach((article) => {
      article.classList.remove("print-article");
    });

    const queryText = `[data-cueid="${cueId}"]`;
    const document = window.document.querySelector(queryText);

    document?.classList.add("print-article");

    window.print();
  };

  const handleOnClickSendData = (trackingObject: TrackingObject) => {
    if (!trackingObject) return;

    if (typeof window === "undefined") return;

    if (trackingObject?.eventLabel === undefined) {
      trackingObject.eventLabel = window.location.href;
    }

    sendTrackingData(trackingObject);
  };

  return (
    <Popover className={"relative block flex-none"} data-testid={testId}>
      <PopoverButton
        data-testid="article-popover-button"
        className={cn(
          COMMON_BUTTON_CSS,
          REMOVE_FOCUS_CSS,
          "aspect-square",
          className
        )}
      >
        <IconImg icon={IconTypeEnum.more} iconSize={24} imgAlt="more tools" />
      </PopoverButton>

      <Transition>
        <TransitionChild
          as={Fragment}
          enter="transition ease duration-500 transform"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease duration-150 transform"
          leaveFrom="opacity-100 "
        >
          <PopoverPanel
            data-testid="article-popover-panel"
            anchor={{ to: isScreenLG ? "bottom start" : "bottom end" }}
            className={cn(
              "relative z-[100000] divide-y divide-gray-250 overflow-hidden rounded-md bg-white shadow-[0px_0px_32px_0px_rgba(0,_0,_0,_0.20)] lg:divide-y-0",
              "no-print lg:py-3"
            )}
          >
            {({ close }) => (
              <>
                <div>
                  <Button
                    buttonSize={ButtonSize.medium}
                    className={cn(BUTTON_CSS)}
                    onClick={handleShareArticle}
                    actionType={ActionType.actionButton}
                    testId="share-button-component"
                  >
                    <p className="mb-0">Share article</p>

                    <IconImg
                      icon={IconTypeEnum.shareLine}
                      iconSize={24}
                      imgAlt="play icon"
                    />
                  </Button>
                </div>

                <div>
                  <Button
                    buttonSize={ButtonSize.medium}
                    className={cn(BUTTON_CSS)}
                    onClick={() => {
                      if (handleOpenModal)
                        handleOpenModal(IconTypeEnum.bookmark);

                      handleOnClickSendData(TRACKING_OBJECT.bookmark);
                    }}
                    actionType={ActionType.actionButton}
                    testId="bookmark-button-component"
                  >
                    <p className="mb-0">Add to my bookmarks</p>

                    <IconImg
                      icon={IconTypeEnum.bookmark}
                      iconSize={24}
                      imgAlt="play icon"
                    />
                  </Button>
                </div>

                <>
                  {!isChinese ? (
                    <div>
                      <ArticleSummariseButton
                        articleId={cueId}
                        isSummaryAvailable={isSummaryAvailable}
                        className={cn(
                          BUTTON_CSS,
                          "flex-row-reverse lg:flex-row lg:justify-start"
                        )}
                      />
                    </div>
                  ) : null}
                </>

                <div>
                  <Button
                    buttonSize={ButtonSize.medium}
                    className={cn(BUTTON_CSS)}
                    onClick={() => {
                      if (handleOpenModal) handleOpenModal(IconTypeEnum.play);
                      handleOnClickSendData(TRACKING_OBJECT.listen);
                    }}
                    actionType={ActionType.actionButton}
                    testId="listen-button-component"
                  >
                    <p className="mb-0">Listen to this article</p>

                    <IconImg
                      icon={IconTypeEnum.playLine}
                      iconSize={24}
                      imgAlt="play icon"
                    />
                  </Button>
                </div>

                {isPremium && handleGenerateGiftURLModal && setIsModalOpen ? (
                  <ArticleSubshare
                    isPremium={isPremium}
                    subshareVersion={GiftAnArticleVersion.version3}
                    handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                    setIsModalOpen={setIsModalOpen}
                    alacrityRes={alacrityRes}
                    buttonClassname={BUTTON_CSS}
                  />
                ) : null}

                {isScreenLG ? (
                  <div>
                    <Button
                      buttonSize={ButtonSize.medium}
                      actionType={ActionType.actionButton}
                      className={cn(BUTTON_CSS)}
                      onClick={() => {
                        close();
                        handlePrint();
                        handleOnClickSendData(TRACKING_OBJECT.print);
                      }}
                      testId="print-button-component"
                    >
                      <p className="mb-0">Print this article</p>

                      <IconImg
                        icon={IconTypeEnum.print}
                        iconSize={24}
                        imgAlt="print icon"
                      />
                    </Button>
                  </div>
                ) : null}

                <div>
                  <Button
                    buttonSize={ButtonSize.medium}
                    className={cn(BUTTON_CSS)}
                    onClick={() => {
                      if (handleOpenModal)
                        handleOpenModal(IconTypeEnum.preferences);
                      handleOnClickSendData(TRACKING_OBJECT.reading);
                    }}
                    actionType={ActionType.actionButton}
                    testId="reading-button-component"
                  >
                    <p className="mb-0">Reading Preferences</p>

                    <IconImg
                      icon={IconTypeEnum.preferences}
                      iconSize={24}
                      imgAlt="preferences icon"
                    />
                  </Button>
                </div>

                {keyword && keyword.length >= 1 ? (
                  <ArticleKeywords
                    keywords={keyword}
                    keywordDisplayOption={KeywordDisplayOption.ToolBar}
                  />
                ) : null}

                <div>
                  <ButtonFactory
                    buttonType={ButtonType.Link}
                    onClick={() => {
                      handleOnClickSendData(TRACKING_OBJECT.feedback);
                    }}
                    link={sourceUrl}
                    className={cn(BUTTON_CSS)}
                  >
                    <p className="mb-0">Feedback</p>

                    <IconImg
                      icon={IconTypeEnum.feedback}
                      iconSize={24}
                      imgAlt="follow icon"
                    />
                  </ButtonFactory>
                </div>

                {displayPurchase &&
                displayPurchase.isDisplayed &&
                encodedTitle &&
                date ? (
                  <div>
                    <ButtonFactory
                      buttonType={ButtonType.Link}
                      onClick={() => {
                        handleOnClickSendData(TRACKING_OBJECT.reuse);
                      }}
                      link={newspostFullUrl}
                      className={cn(BUTTON_CSS)}
                    >
                      <p className="mb-0">Reuse this content</p>

                      <IconImg
                        icon={IconTypeEnum.reuse}
                        iconSize={24}
                        imgAlt="reuse icon"
                      />
                    </ButtonFactory>
                  </div>
                ) : null}
              </>
            )}
          </PopoverPanel>
        </TransitionChild>
      </Transition>
    </Popover>
  );
}
